// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { Icon } from "components/ShellV2";

function Metric({ label, value, icon }) {
  return (
    <div className="rec-dso-metric">
      <div className="rec-dso-metric-top">
        <span className="rec-dso-metric-lbl">{label}</span>
        <Icon name={icon} style={{ fontSize: 17, color: "var(--ink-3)", opacity: 0.6 }} />
      </div>
      <div className="rec-dso-metric-val">{value}</div>
    </div>
  );
}

Metric.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.string.isRequired,
};

function KeyMetrics({ data, selectedMember }) {
  const { t } = useTranslation(["sizing"]);
  const calculateTotal = (values) => values.reduce((sum, value) => sum + value, 0).toFixed(2);

  let metrics = [];

  if (!selectedMember || selectedMember === "all") {
    const uniqueMeters = [...new Set(data.map((entry) => entry.meter_id))];

    const totalCost =
      data.length > 0 && data[0].obj_value !== undefined ? data[0].obj_value.toFixed(2) : "0.00";

    const sumByMeter = (field) =>
      calculateTotal(
        uniqueMeters.map((meterId) => {
          const meterEntry = data.find((entry) => entry.meter_id === meterId);
          return meterEntry ? meterEntry[field] ?? 0 : 0;
        })
      );

    metrics = [
      {
        title: t("results.metrics.titles.communityTotalCost"),
        value: `${totalCost} EUR`,
        icon: "savings",
      },
      {
        title: t("results.metrics.titles.pvGeneration"),
        value: `${calculateTotal(data.map((entry) => entry.e_g ?? 0))} kWh`,
        icon: "solar_power",
      },
      {
        title: t("results.metrics.titles.energyBought"),
        value: `${calculateTotal(data.map((entry) => entry.e_sup ?? 0))} kWh`,
        icon: "energy_savings_leaf",
      },
      {
        title: t("results.metrics.titles.energySold"),
        value: `${calculateTotal(data.map((entry) => entry.e_sur ?? 0))} kWh`,
        icon: "energy_savings_leaf",
      },
      {
        title: t("results.metrics.titles.batteryCapacity"),
        value: `${sumByMeter("e_bn_total")} kWh`,
        icon: "battery_charging_full",
      },
      {
        title: t("results.metrics.titles.additionalBatteryCapacity"),
        value: `${sumByMeter("e_bn_new")} kWh`,
        icon: "battery_charging_full",
      },
      {
        title: t("results.metrics.titles.pvCapacity"),
        value: `${sumByMeter("p_gn_total")} kW`,
        icon: "solar_power",
      },
      {
        title: t("results.metrics.titles.additionalPvCapacity"),
        value: `${sumByMeter("p_gn_new")} kW`,
        icon: "solar_power",
      },
    ];
  } else {
    const memberData = data.filter((entry) => entry.meter_id === selectedMember);
    if (memberData.length > 0) {
      const firstEntry = memberData[0];
      metrics = [
        {
          title: t("results.metrics.titles.batteryCapacity"),
          value: `${firstEntry.e_bn_total.toFixed(2)} kWh`,
          icon: "battery_charging_full",
        },
        {
          title: t("results.metrics.titles.additionalBatteryCapacity"),
          value: `${firstEntry.e_bn_new?.toFixed(2) ?? "0.00"} kWh`,
          icon: "battery_charging_full",
        },
        {
          title: t("results.metrics.titles.pvCapacity"),
          value: `${firstEntry.p_gn_total.toFixed(2)} kW`,
          icon: "solar_power",
        },
        {
          title: t("results.metrics.titles.additionalPvCapacity"),
          value: `${firstEntry.p_gn_new?.toFixed(2) ?? "0.00"} kWh`,
          icon: "solar_power",
        },
        {
          title: t("results.metrics.titles.energyBought"),
          value: `${calculateTotal(memberData.map((entry) => entry.e_sup ?? 0))} kWh`,
          icon: "energy_savings_leaf",
        },
        {
          title: t("results.metrics.titles.energySold"),
          value: `${calculateTotal(memberData.map((entry) => entry.e_sur ?? 0))} kWh`,
          icon: "energy_savings_leaf",
        },
        {
          title: t("results.metrics.titles.pvGeneration"),
          value: `${calculateTotal(memberData.map((entry) => entry.e_g ?? 0))} kWh`,
          icon: "solar_power",
        },
        {
          title: t("results.metrics.titles.energyCost"),
          value: `${firstEntry.c_ind2pool.toFixed(2)} EUR`,
          icon: "savings",
        },
      ];
    }
  }

  if (metrics.length === 0) return null;

  const topRow = metrics.slice(0, 4);
  const bottomRow = metrics.slice(4);

  return (
    <div className="rec-card rec-dso-metrics">
      <div
        className="rec-dso-metric-row"
        style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
      >
        {topRow.map((m) => (
          <Metric key={m.title} label={m.title} value={m.value} icon={m.icon} />
        ))}
      </div>
      {bottomRow.length > 0 && (
        <>
          <div className="rec-dso-metric-div" />
          <div
            className="rec-dso-metric-row"
            style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
          >
            {bottomRow.map((m) => (
              <Metric key={m.title} label={m.title} value={m.value} icon={m.icon} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

KeyMetrics.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      datetime_timestamp: PropTypes.string.isRequired,
      obj_value: PropTypes.number,
      e_g: PropTypes.number,
      e_sup: PropTypes.number,
      e_sur: PropTypes.number,
      e_bat: PropTypes.number,
      e_bn_total: PropTypes.number,
      p_gn_total: PropTypes.number,
      e_bn_new: PropTypes.number,
      p_gn_new: PropTypes.number,
      c_ind2pool: PropTypes.number,
    })
  ).isRequired,
  selectedMember: PropTypes.string.isRequired,
};

export default KeyMetrics;
